import React, { useCallback, useRef, useState } from 'react'

import { Box, DisplayPosition, ZIndex, withCitrusTheme } from '@gousto-internal/citrus-react'

import { Collapsed } from './components/Collapsed'
import { EditBoxSizeModal } from './components/EditBoxSizeModal'
import { EditDeliveryAddressModal } from './components/EditDeliveryAddressModal'
import { Expanded } from './components/Expanded'
import { useDeliverySettings } from './useDeliverySettings'
import { useDismissOnOutside } from './useDismissOnOutside'
import { usePublishCssVarHeight } from './usePublishCssVarHeight'

const ExpandedPanel = withCitrusTheme(Box, () => ({
  animation: 'deliverySettingsDropdownFadeIn 200ms ease-out',
  '@keyframes deliverySettingsDropdownFadeIn': {
    from: { opacity: 0, transform: 'translateY(-8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}))

const ExpandedPanelClosing = withCitrusTheme(Box, () => ({
  animation: 'deliverySettingsDropdownFadeOut 200ms ease-in forwards',
  '@keyframes deliverySettingsDropdownFadeOut': {
    from: { opacity: 1, transform: 'translateY(0)' },
    to: { opacity: 0, transform: 'translateY(-8px)' },
  },
}))

export const DeliverySettingsDropdown = () => {
  const { isEnabled, boxSize, deliveryDayShort, deliveryDayLong, postcode, fullAddress } =
    useDeliverySettings()
  const [isExpanded, setIsExpanded] = useState(false)
  // isClosing keeps the panel mounted through its fade-out animation before unmounting.
  const [isClosing, setIsClosing] = useState(false)
  const [isEditBoxSizeModalOpen, setIsEditBoxSizeModalOpen] = useState(false)
  const [isEditDeliveryAddressModalOpen, setIsEditDeliveryAddressModalOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setIsExpanded(false)
    setIsClosing(true)
  }, [])

  const toggle = () => {
    if (isExpanded) {
      close()
    } else {
      setIsClosing(false) // cancel any in-flight close animation
      setIsExpanded(true)
    }
  }

  const isAnyModalOpen = isEditBoxSizeModalOpen || isEditDeliveryAddressModalOpen
  useDismissOnOutside(wrapperRef, isExpanded && !isAnyModalOpen, close)
  // Consumed by MenuPage's sticky NavBar to offset its top.
  usePublishCssVarHeight(wrapperRef, '--delivery-settings-dropdown-height', isEnabled)

  if (!isEnabled) return null

  return (
    <Box ref={wrapperRef} zIndex={ZIndex.Dropdown} top="0" style={{ position: 'sticky' }}>
      <Collapsed
        boxSize={boxSize}
        deliveryDay={deliveryDayShort}
        address={postcode}
        isExpanded={isExpanded}
        onEditClick={toggle}
      />
      {(isExpanded || isClosing) &&
        (isClosing ? (
          <ExpandedPanelClosing
            position={DisplayPosition.Absolute}
            top="100%"
            left="0"
            right="0"
            onAnimationEnd={() => setIsClosing(false)}
          >
            <Expanded
              boxSize={boxSize}
              deliveryDay={deliveryDayLong}
              address={fullAddress}
              onEditBoxSize={() => setIsEditBoxSizeModalOpen(true)}
              onEditAddress={() => setIsEditDeliveryAddressModalOpen(true)}
              onRowConfirmed={close}
            />
          </ExpandedPanelClosing>
        ) : (
          <ExpandedPanel position={DisplayPosition.Absolute} top="100%" left="0" right="0">
            <Expanded
              boxSize={boxSize}
              deliveryDay={deliveryDayLong}
              address={fullAddress}
              onEditBoxSize={() => setIsEditBoxSizeModalOpen(true)}
              onEditAddress={() => setIsEditDeliveryAddressModalOpen(true)}
              onRowConfirmed={close}
            />
          </ExpandedPanel>
        ))}
      {isEditBoxSizeModalOpen && (
        <EditBoxSizeModal
          isOpen
          onClose={() => setIsEditBoxSizeModalOpen(false)}
          onConfirmComplete={() => {
            setIsEditBoxSizeModalOpen(false)
            close()
          }}
        />
      )}
      {isEditDeliveryAddressModalOpen && (
        <EditDeliveryAddressModal
          isOpen
          onClose={() => setIsEditDeliveryAddressModalOpen(false)}
          onConfirmComplete={() => {
            setIsEditDeliveryAddressModalOpen(false)
            close()
          }}
        />
      )}
    </Box>
  )
}
